/**
 * SIE Upload Utilities
 * 
 * Shared utilities for handling SIE file uploads and encoding detection.
 * Used by both SieAccountingBasisPage and ModuleFilesTab for multi-file support.
 */

/**
 * Decodes a SIE file with proper character encoding detection.
 * SIE standard specifies CP437 (IBM PC 8-bit) but modern Swedish systems
 * often use ISO-8859-1 or Windows-1252.
 */
export async function decodeSieFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  
  // CP437 (IBM PC 8-bit) decoding table for Swedish characters
  const cp437ToUnicode: Record<number, string> = {
    0x84: 'ä', 0x86: 'å', 0x8E: 'Ä', 0x8F: 'Å',
    0x94: 'ö', 0x99: 'Ö', 0x81: 'ü', 0x9A: 'Ü',
    0x82: 'é', 0x90: 'É',
  };
  
  const decodeCP437 = (data: Uint8Array): string => {
    let result = '';
    for (let i = 0; i < data.length; i++) {
      const byte = data[i];
      if (cp437ToUnicode[byte]) {
        result += cp437ToUnicode[byte];
      } else if (byte < 128) {
        result += String.fromCharCode(byte);
      } else {
        result += String.fromCharCode(byte);
      }
    }
    return result;
  };
  
  // Check for UTF-8 BOM (EF BB BF)
  const hasUtf8BOM = bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF;
  
  if (hasUtf8BOM) {
    return new TextDecoder('utf-8').decode(buffer);
  }
  
  // Detect encoding based on Swedish character byte patterns
  const hasCP437SwedishBytes = bytes.some(b => 
    b === 0x84 || b === 0x86 || b === 0x94 || b === 0x8E || b === 0x8F || b === 0x99
  );
  const hasISO88591SwedishBytes = bytes.some(b => 
    b === 0xE4 || b === 0xE5 || b === 0xF6 || b === 0xC4 || b === 0xC5 || b === 0xD6
  );
  
  if (hasCP437SwedishBytes && !hasISO88591SwedishBytes) {
    return decodeCP437(bytes);
  } else if (hasISO88591SwedishBytes) {
    return new TextDecoder('iso-8859-1').decode(buffer);
  }
  
  // Fallback to Windows-1252
  return new TextDecoder('windows-1252').decode(buffer);
}

/**
 * Validates that a file appears to be a valid SIE file.
 * Checks for presence of required SIE tags.
 */
export function validateSieContent(content: string): { valid: boolean; error?: string } {
  if (!content || content.length < 50) {
    return { valid: false, error: 'Filen verkar vara tom eller för kort' };
  }
  
  // SIE files should start with #FLAGGA or contain #VER, #KONTO, etc.
  const hasValidTags = content.includes('#FLAGGA') || 
                       content.includes('#VER') || 
                       content.includes('#KONTO') ||
                       content.includes('#RAR') ||
                       content.includes('#FNAMN');
  
  if (!hasValidTags) {
    return { valid: false, error: 'Filen saknar giltiga SIE-taggar' };
  }
  
  return { valid: true };
}

/**
 * Checks if a file is a SIE file based on extension or MIME type
 */
export function isSieFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.se') || 
         name.endsWith('.si') || 
         name.endsWith('.sie') ||
         name.endsWith('.sie4') ||
         file.type === 'text/plain';
}
